import { useMemo } from "react";
import type { BuyerAutoDistributeResultDto } from "@/api/buyers";
import { Modal } from "@/components/ui/Modal";
import {
  Button,
  EmptyState,
  Skeleton,
  SubTable,
  type SubTableColumn,
} from "@/design-system/components/ui";

/**
 * The plan an Auto-Distribute run WOULD carry out, shown before anything moves.
 *
 * Preview-before-apply is the point of this screen, not a courtesy: the run
 * reassigns every unassigned buyer at once, and the queue is the only place a
 * new buyer appears — so a manager has to be able to see who ends up carrying
 * what, and which buyers nobody has earned yet, while it is still undoable by
 * simply not pressing the button.
 */
export interface AutoDistributePreviewModalProps {
  open: boolean;
  onClose: () => void;
  /** The `preview: true` result. Null while it is still being computed. */
  preview: BuyerAutoDistributeResultDto | null;
  /** The preview call is in flight. */
  loading?: boolean;
  /** The `preview: false` call is in flight. */
  applying?: boolean;
  onConfirm: () => void;
}

/** One specialist's share of the planned run, with the standing it was decided from. */
interface PlannedShare {
  specialistUserId: string;
  displayName: string;
  count: number;
  currentlyHeld: number;
  entitlement: number;
  towardNext: number;
  qualifyingThreshold: number;
}

/**
 * The plan row.
 *
 * Counts are right-aligned and tabular; "+N" keeps its sign because the whole
 * table is about what a run WOULD ADD, and a bare number reads as a total.
 */
const PLAN_COLUMNS: SubTableColumn<PlannedShare>[] = [
  { key: "specialist", header: "Consultant", render: (s) => s.displayName },
  { key: "count", header: "Buyers in this run", align: "right", render: (s) => `+${s.count}` },
  { key: "held", header: "Carrying now", align: "right", render: (s) => s.currentlyHeld },
  { key: "entitlement", header: "Entitled to", align: "right", render: (s) => s.entitlement },
  {
    key: "towardNext",
    header: "Toward next",
    align: "right",
    className: "text-gray-mid",
    render: (s) => `${s.towardNext} of ${s.qualifyingThreshold}`,
  },
];

/** The queue remainder, folded by reason — a 200-buyer queue that nobody has
 *  earned produces ONE line here rather than 200 identical ones. */
interface Remainder {
  reason: string;
  names: string[];
}

export function AutoDistributePreviewModal({
  open,
  onClose,
  preview,
  loading = false,
  applying = false,
  onConfirm,
}: AutoDistributePreviewModalProps) {
  const plan = useMemo(() => buildPlan(preview), [preview]);
  const remainder = useMemo(() => buildRemainder(preview), [preview]);

  // 🔴 A preview reports `assigned: 0` by construction — the server has not
  // assigned anything. The plan size lives in `items`, so reading the headline
  // off `preview.assigned` would tell every manager the run does nothing.
  const plannedCount = preview?.items.filter((i) => i.assigned).length ?? 0;
  const remainingCount = (preview?.considered ?? 0) - plannedCount;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Auto-distribute the queue"
      description="Nothing has moved yet. This is what the run would do."
      size="lg"
      footer={
        <div className="flex items-center justify-end gap-space-2">
          <Button variant="secondary" onClick={onClose} disabled={applying}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={applying || loading || plannedCount === 0}>
            {applying
              ? "Assigning…"
              : plannedCount === 1
                ? "Assign 1 buyer"
                : `Assign ${plannedCount} buyers`}
          </Button>
        </div>
      }
    >
      {loading || !preview ? (
        <Skeleton className="h-48 w-full" />
      ) : preview.considered === 0 ? (
        <EmptyState
          title="The queue is clear"
          description="There are no unassigned buyers to distribute."
        />
      ) : (
        <div className="flex flex-col gap-space-5">
          <p className="text-small text-onyx" data-testid="auto-distribute-summary">
            <strong>{preview.considered}</strong> in the queue ·{" "}
            <strong>{plannedCount}</strong> would be assigned ·{" "}
            <strong>{remainingCount}</strong> would stay
          </p>

          {plan.length === 0 ? (
            <EmptyState
              title="Nobody is eligible right now"
              description="Every consultant is already carrying at least what they have earned. Raise the allocation policy, or assign by hand."
            />
          ) : (
            <section>
              <h3 className="font-display text-lead text-onyx mb-space-2">Who gets what</h3>
              {/* Inside a modal, so the surface probe resolves "drawer" and the
                  header renders tinted rather than painting the page's own onyx
                  bar inside an overlay. Nothing declares it here — that is the
                  point of resolving it from context. */}
              <SubTable<PlannedShare>
                caption="Each consultant's share of this run, and the standing it was decided from"
                data-testid="auto-distribute-plan"
                rows={plan}
                rowKey={(s) => s.specialistUserId}
                columns={PLAN_COLUMNS}
              />
            </section>
          )}

          {remainder.length > 0 && (
            <section>
              <h3 className="font-display text-lead text-onyx mb-space-2">
                Staying in the queue
              </h3>
              {/* The remainder is a normal outcome — the queue is longer than the
                  book the team has earned — so it is reported, not hidden. */}
              <ul className="flex flex-col gap-space-3 list-none p-0 m-0">
                {remainder.map((r) => (
                  <li
                    key={r.reason}
                    className="border border-border-light rounded-md bg-white-warm p-space-3"
                  >
                    <p className="text-small text-onyx">
                      {r.names.length} {r.names.length === 1 ? "buyer" : "buyers"} — {r.reason}
                    </p>
                    <p className="text-caption text-gray-mid mt-space-1">
                      {summarizeNames(r.names)}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <p className="text-caption text-gray-mid">
            The run is recomputed when it is applied, so a buyer assigned by hand in
            the meantime is left alone and reported as such.
          </p>
        </div>
      )}
    </Modal>
  );
}

function buildPlan(preview: BuyerAutoDistributeResultDto | null): PlannedShare[] {
  if (!preview) return [];

  const counts = new Map<string, number>();
  for (const item of preview.items) {
    if (!item.assigned || !item.specialistUserId) continue;
    counts.set(item.specialistUserId, (counts.get(item.specialistUserId) ?? 0) + 1);
  }

  // The standings carry the numbers a manager needs to judge the plan; the item
  // list carries the display name for anyone the standings somehow miss.
  const standings = new Map(preview.specialists.map((s) => [s.specialistUserId, s]));
  const fallbackNames = new Map(
    preview.items
      .filter((i) => i.specialistUserId)
      .map((i) => [i.specialistUserId as string, i.specialistDisplayName ?? null]),
  );

  return [...counts.entries()]
    .map(([specialistUserId, count]) => {
      const standing = standings.get(specialistUserId);
      return {
        specialistUserId,
        displayName:
          standing?.displayName ??
          fallbackNames.get(specialistUserId) ??
          specialistUserId,
        count,
        currentlyHeld: standing?.currentlyHeld ?? 0,
        entitlement: standing?.entitlement ?? 0,
        towardNext: standing?.towardNext ?? 0,
        qualifyingThreshold: standing?.qualifyingThreshold ?? 0,
      };
    })
    .sort((a, b) => b.count - a.count || a.displayName.localeCompare(b.displayName));
}

function buildRemainder(preview: BuyerAutoDistributeResultDto | null): Remainder[] {
  if (!preview) return [];

  const byReason = new Map<string, string[]>();
  for (const item of preview.items) {
    if (item.assigned) continue;
    const reason = item.reason ?? "No reason given.";
    const names = byReason.get(reason) ?? [];
    names.push(item.buyerName ?? "(no name)");
    byReason.set(reason, names);
  }

  return [...byReason.entries()]
    .map(([reason, names]) => ({ reason, names }))
    .sort((a, b) => b.names.length - a.names.length);
}

/** Names enough to recognise the group, without pouring a whole queue on screen. */
function summarizeNames(names: string[]): string {
  const shown = names.slice(0, 5);
  const rest = names.length - shown.length;
  return rest > 0 ? `${shown.join(", ")} +${rest} more` : shown.join(", ");
}
